import React, { useCallback, useMemo, useRef, useState } from 'react';
import Calendar from '../app/Calendar';
import { SNAP_MINUTES } from '../shared/dayTimelineUtils';
import {
  CoverageGap,
  PRICING_COLUMNS,
  PRICING_TIMELINE_HEIGHT_PX,
  PricingBlock,
  PricingRuleRecord,
  PUBLIC_HOLIDAY_DAY,
  blocksForDay,
  clampBlockToDay,
  dateKey,
  findCoverageGaps,
  formatMinutesRange,
  getActiveRules,
  getRulesEffectiveOnDate,
  hasFullCoverage,
  MINUTES_PER_DAY,
  minutesToY,
  newLocalId,
  rateColor,
  resolveDayOfWeekForDate,
  rulesToBlocks,
  snapMinutes,
  yToMinutes,
} from './pricingRulesUtils';

const HOUR_ROWS = Array.from({ length: 24 }, (_, hour) => hour);
const DEFAULT_RATE = 50;

type DragMode = 'create' | 'move' | 'resize-top' | 'resize-bottom';

interface ColumnDragState {
  dayOfWeek: number;
  mode: DragMode;
  blockId: string | null;
  anchorMinutes: number;
  originalBlock: PricingBlock | null;
}

interface PricingRulesGridProps {
  buildingId: string;
  rules: PricingRuleRecord[];
  loading: boolean;
  saving: boolean;
  onSave: (blocks: PricingBlock[], publicHolidayDates: string[], effectiveFrom: string) => Promise<void>;
}

function PricingBlockBox({
  block,
  minRate,
  maxRate,
  editMode,
  selected,
  onSelect,
  onDelete,
  onPointerDownMove,
  onPointerDownResizeTop,
  onPointerDownResizeBottom,
}: {
  block: PricingBlock;
  minRate: number;
  maxRate: number;
  editMode: boolean;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onPointerDownMove: (e: React.PointerEvent) => void;
  onPointerDownResizeTop: (e: React.PointerEvent) => void;
  onPointerDownResizeBottom: (e: React.PointerEvent) => void;
}) {
  const top = minutesToY(block.startMinutes);
  const height = Math.max(minutesToY(block.endMinutes) - top, 8);
  const bg = rateColor(block.hourlyRate, minRate, maxRate);

  return (
    <div
      className={`pricing-block ${selected ? 'pricing-block-selected' : ''}`}
      style={{
        position: 'absolute',
        left: 2,
        right: 2,
        top,
        height,
        borderRadius: 4,
        backgroundColor: bg,
        opacity: editMode ? 0.92 : 0.85,
        border: selected ? '2px solid var(--primary, #3182ce)' : '1px solid rgba(0,0,0,0.15)',
        cursor: editMode ? 'grab' : 'default',
        overflow: 'hidden',
        fontSize: 10,
        fontWeight: 600,
        color: '#1a202c',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 4px',
        boxSizing: 'border-box',
        zIndex: selected ? 3 : 2,
      }}
      title={`${formatMinutesRange(block.startMinutes, block.endMinutes)} @ $${block.hourlyRate.toFixed(2)}/hr`}
      onClick={(e) => {
        e.stopPropagation();
        if (editMode) onSelect();
      }}
      onPointerDown={(e) => {
        if (!editMode) return;
        if ((e.target as HTMLElement).dataset.handle) return;
        e.stopPropagation();
        onSelect();
        onPointerDownMove(e);
      }}
    >
      {editMode && selected && (
        <button
          type="button"
          className="pricing-block-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete pricing block"
        >
          ×
        </button>
      )}
      <div style={{ textAlign: 'center', lineHeight: 1.2, pointerEvents: 'none' }}>
        <div>${block.hourlyRate.toFixed(0)}/hr</div>
        {height > 28 && (
          <div style={{ fontSize: 9, fontWeight: 500, opacity: 0.85 }}>
            {formatMinutesRange(block.startMinutes, block.endMinutes)}
          </div>
        )}
      </div>
      {editMode && selected && (
        <>
          <div
            data-handle="top"
            className="pricing-block-handle pricing-block-handle-top"
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDownResizeTop(e);
            }}
          />
          <div
            data-handle="bottom"
            className="pricing-block-handle pricing-block-handle-bottom"
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDownResizeBottom(e);
            }}
          />
        </>
      )}
    </div>
  );
}

function PricingColumnTrack({
  dayOfWeek,
  blocks,
  editMode,
  minRate,
  maxRate,
  selectedBlockId,
  onSelectBlock,
  onUpdateBlocks,
  onDeleteBlock,
}: {
  dayOfWeek: number;
  blocks: PricingBlock[];
  editMode: boolean;
  minRate: number;
  maxRate: number;
  selectedBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
  onUpdateBlocks: (updater: (prev: PricingBlock[]) => PricingBlock[]) => void;
  onDeleteBlock: (id: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<ColumnDragState | null>(null);
  const [creatingPreview, setCreatingPreview] = useState<{ startMinutes: number; endMinutes: number } | null>(null);

  const columnBlocks = useMemo(() => blocksForDay(blocks, dayOfWeek), [blocks, dayOfWeek]);

  const getMinutesFromEvent = useCallback((clientY: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const y = Math.max(0, Math.min(PRICING_TIMELINE_HEIGHT_PX, clientY - rect.top));
    return yToMinutes(y);
  }, []);

  const wouldOverlap = useCallback(
    (candidate: PricingBlock, excludeId?: string) =>
      blocks.some((b) => b.dayOfWeek === dayOfWeek && b.localId !== excludeId && b.localId !== candidate.localId && b.startMinutes < candidate.endMinutes && candidate.startMinutes < b.endMinutes),
    [blocks, dayOfWeek],
  );

  const applyBlockUpdate = useCallback(
    (updated: PricingBlock) => {
      const clamped = clampBlockToDay(updated);
      if (wouldOverlap(clamped, updated.localId)) return;
      onUpdateBlocks((prev) => prev.map((b) => (b.localId === updated.localId ? clamped : b)));
    },
    [onUpdateBlocks, wouldOverlap],
  );

  const finishDrag = useCallback(() => {
    dragRef.current = null;
    setCreatingPreview(null);
  }, []);

  const handlePointerDownCreate = (e: React.PointerEvent) => {
    if (!editMode) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const anchor = getMinutesFromEvent(e.clientY);
    dragRef.current = { dayOfWeek, mode: 'create', blockId: null, anchorMinutes: anchor, originalBlock: null };
    setCreatingPreview({ startMinutes: anchor, endMinutes: anchor + SNAP_MINUTES });
  };

  const handlePointerDownMove = (block: PricingBlock) => (e: React.PointerEvent) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      dayOfWeek,
      mode: 'move',
      blockId: block.localId,
      anchorMinutes: getMinutesFromEvent(e.clientY),
      originalBlock: { ...block },
    };
  };

  const handlePointerDownResize = (block: PricingBlock, edge: 'top' | 'bottom') => (e: React.PointerEvent) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      dayOfWeek,
      mode: edge === 'top' ? 'resize-top' : 'resize-bottom',
      blockId: block.localId,
      anchorMinutes: edge === 'top' ? block.startMinutes : block.endMinutes,
      originalBlock: { ...block },
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.dayOfWeek !== dayOfWeek) return;
    e.preventDefault();
    const currentMinutes = getMinutesFromEvent(e.clientY);

    if (drag.mode === 'create') {
      const start = Math.min(drag.anchorMinutes, currentMinutes);
      const end = Math.max(drag.anchorMinutes, currentMinutes);
      setCreatingPreview({ startMinutes: start, endMinutes: Math.max(end, start + SNAP_MINUTES) });
      return;
    }

    if (!drag.originalBlock) return;
    const orig = drag.originalBlock;

    if (drag.mode === 'move') {
      const delta = currentMinutes - drag.anchorMinutes;
      const duration = orig.endMinutes - orig.startMinutes;
      let newStart = snapMinutes(orig.startMinutes + delta);
      let newEnd = newStart + duration;
      if (newEnd > MINUTES_PER_DAY) {
        newEnd = MINUTES_PER_DAY;
        newStart = Math.max(0, newEnd - duration);
      }
      applyBlockUpdate({ ...orig, startMinutes: newStart, endMinutes: newEnd });
    } else if (drag.mode === 'resize-top') {
      applyBlockUpdate({ ...orig, startMinutes: currentMinutes });
    } else if (drag.mode === 'resize-bottom') {
      applyBlockUpdate({ ...orig, endMinutes: currentMinutes });
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.dayOfWeek !== dayOfWeek) return;
    e.preventDefault();

    if (drag.mode === 'create' && creatingPreview) {
      const start = Math.min(creatingPreview.startMinutes, creatingPreview.endMinutes);
      const end = Math.max(creatingPreview.startMinutes, creatingPreview.endMinutes);
      const candidate = clampBlockToDay({
        localId: newLocalId(),
        dayOfWeek,
        startMinutes: start,
        endMinutes: Math.max(end, start + SNAP_MINUTES),
        hourlyRate: DEFAULT_RATE,
      });
      if (!wouldOverlap(candidate)) {
        onUpdateBlocks((prev) => [...prev, candidate]);
        onSelectBlock(candidate.localId);
      }
    } else if (drag.originalBlock && drag.blockId) {
      const current = blocks.find((b) => b.localId === drag.blockId);
      if (current) applyBlockUpdate(current);
    }

    finishDrag();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const previewTop = creatingPreview ? minutesToY(creatingPreview.startMinutes) : 0;
  const previewHeight = creatingPreview
    ? Math.max(minutesToY(creatingPreview.endMinutes) - previewTop, 6)
    : 0;

  return (
    <div
      ref={trackRef}
      className="pricing-column-track"
      style={{
        position: 'relative',
        height: PRICING_TIMELINE_HEIGHT_PX,
        background: 'linear-gradient(to bottom, #f7fafc 0%, #edf2f7 100%)',
        cursor: editMode ? 'crosshair' : 'default',
        touchAction: editMode ? 'none' : 'auto',
        borderLeft: '1px solid var(--border)',
      }}
      onPointerDown={editMode ? handlePointerDownCreate : undefined}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={() => editMode && onSelectBlock(null)}
    >
      {HOUR_ROWS.map((hour) => (
        <div
          key={hour}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: (hour / 24) * PRICING_TIMELINE_HEIGHT_PX,
            height: PRICING_TIMELINE_HEIGHT_PX / 24,
            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
            pointerEvents: 'none',
          }}
        />
      ))}

      {columnBlocks.map((block) => (
        <PricingBlockBox
          key={block.localId}
          block={block}
          minRate={minRate}
          maxRate={maxRate}
          editMode={editMode}
          selected={selectedBlockId === block.localId}
          onSelect={() => onSelectBlock(block.localId)}
          onDelete={() => onDeleteBlock(block.localId)}
          onPointerDownMove={handlePointerDownMove(block)}
          onPointerDownResizeTop={handlePointerDownResize(block, 'top')}
          onPointerDownResizeBottom={handlePointerDownResize(block, 'bottom')}
        />
      ))}

      {editMode && creatingPreview && (
        <div
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            top: previewTop,
            height: previewHeight,
            borderRadius: 4,
            backgroundColor: 'rgba(49, 130, 206, 0.35)',
            border: '2px dashed var(--primary, #3182ce)',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        />
      )}
    </div>
  );
}

export default function PricingRulesGrid({ buildingId, rules, loading, saving, onSave }: PricingRulesGridProps) {
  const [editMode, setEditMode] = useState(false);
  const [draftBlocks, setDraftBlocks] = useState<PricingBlock[]>([]);
  const publicHolidayDates = useMemo(() => {
    try {
      const raw = localStorage.getItem(`pricing-holidays-${buildingId}`);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set<string>();
    }
  }, [buildingId]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [previewDate, setPreviewDate] = useState<Date>(new Date());
  const [effectiveFromDate, setEffectiveFromDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [rateInput, setRateInput] = useState('');
  const [exitError, setExitError] = useState('');
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [coverageGaps, setCoverageGaps] = useState<CoverageGap[]>([]);

  const activeRules = useMemo(() => getActiveRules(rules), [rules]);
  const displayBlocks = editMode ? draftBlocks : rulesToBlocks(activeRules);

  const { minRate, maxRate } = useMemo(() => {
    const rates = displayBlocks.map((b) => b.hourlyRate);
    if (rates.length === 0) return { minRate: DEFAULT_RATE, maxRate: DEFAULT_RATE };
    return { minRate: Math.min(...rates), maxRate: Math.max(...rates) };
  }, [displayBlocks]);

  const selectedBlock = useMemo(
    () => (selectedBlockId ? draftBlocks.find((b) => b.localId === selectedBlockId) ?? null : null),
    [draftBlocks, selectedBlockId],
  );

  const previewDayOfWeek = resolveDayOfWeekForDate(previewDate, publicHolidayDates);
  const previewBlocks = blocksForDay(rulesToBlocks(getRulesEffectiveOnDate(rules, previewDate)), previewDayOfWeek);
  const previewColumn = PRICING_COLUMNS.find((c) => c.dayOfWeek === previewDayOfWeek);

  const enterEditMode = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setDraftBlocks(rulesToBlocks(activeRules));
    setEffectiveFromDate(today);
    setSelectedBlockId(null);
    setExitError('');
    setEditMode(true);
  };

  const cancelEditMode = () => {
    if (!window.confirm('Discard unsaved pricing changes?')) return;
    setEditMode(false);
    setDraftBlocks([]);
    setSelectedBlockId(null);
    setExitError('');
    setShowExitConfirm(false);
    setCoverageGaps([]);
  };

  const attemptExitEditMode = () => {
    const gaps = findCoverageGaps(draftBlocks);
    if (gaps.length > 0) {
      setCoverageGaps(gaps);
      setExitError('All time periods must be covered before saving. Fill the gaps highlighted below.');
      return;
    }
    setCoverageGaps([]);
    setExitError('');
    setShowExitConfirm(true);
  };

  const confirmSaveAndExit = async () => {
    if (!hasFullCoverage(draftBlocks)) {
      setCoverageGaps(findCoverageGaps(draftBlocks));
      setExitError('All time periods must be covered before saving.');
      setShowExitConfirm(false);
      return;
    }

    try {
      await onSave(draftBlocks, [...publicHolidayDates], dateKey(effectiveFromDate));
      setEditMode(false);
      setDraftBlocks([]);
      setSelectedBlockId(null);
      setShowExitConfirm(false);
      setExitError('');
      setCoverageGaps([]);
    } catch {
      setShowExitConfirm(false);
    }
  };

  const updateDraftBlocks = useCallback((updater: (prev: PricingBlock[]) => PricingBlock[]) => {
    setDraftBlocks(updater);
  }, []);

  const deleteBlock = useCallback((localId: string, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Delete this pricing block?')) return;
    setDraftBlocks((prev) => prev.filter((b) => b.localId !== localId));
    setSelectedBlockId((current) => (current === localId ? null : current));
  }, []);

  const updateSelectedRate = () => {
    if (!selectedBlock) return;
    const rate = parseFloat(rateInput);
    if (Number.isNaN(rate) || rate < 0) return;
    setDraftBlocks((prev) =>
      prev.map((b) => (b.localId === selectedBlock.localId ? { ...b, hourlyRate: rate } : b)),
    );
  };

  React.useEffect(() => {
    if (selectedBlock) setRateInput(selectedBlock.hourlyRate.toString());
  }, [selectedBlock]);

  React.useEffect(() => {
    if (!editMode || !selectedBlockId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteBlock(selectedBlockId, true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, selectedBlockId, deleteBlock]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}>Loading pricing rules…</div>;
  }

  return (
    <div className="pricing-rules-layout">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Pricing Rules</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
            Weekly schedule with public holiday rates. Each day must cover all 24 hours.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!editMode ? (
            <button type="button" className="ah-btn" onClick={enterEditMode} disabled={saving}>
              Edit mode
            </button>
          ) : (
            <>
              <button type="button" className="ah-btn ah-btn-outline" onClick={cancelEditMode} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="ah-btn" onClick={attemptExitEditMode} disabled={saving}>
                Save &amp; exit edit mode
              </button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <div className="pricing-edit-banner">
          Edit mode is active. Drag on a column to create a block, drag blocks to move them, use handles to resize, and press Delete to remove the selected block.
          Snaps to {SNAP_MINUTES}-minute intervals.
        </div>
      )}

      {exitError && <div className="pricing-error-banner">{exitError}</div>}

      {coverageGaps.length > 0 && (
        <div className="pricing-gap-banner">
          <strong>Uncovered time periods:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {coverageGaps.map((gap, i) => (
              <li key={`${gap.dayOfWeek}-${gap.startMinutes}-${i}`}>
                {gap.label}: {formatMinutesRange(gap.startMinutes, gap.endMinutes)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {editMode && (
        <div className="pricing-effective-from-section">
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Pricing effective from</h3>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              Choose the date when this pricing schedule should take effect. Current pricing remains in place until then.
            </p>
            <Calendar
              selectedDate={effectiveFromDate}
              onDateSelect={setEffectiveFromDate}
              maxDaysFuture={730}
              disabled={saving}
            />
          </div>
          <div className="pricing-effective-from-summary">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>New schedule starts</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {effectiveFromDate.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
              {dateKey(effectiveFromDate) === dateKey(new Date())
                ? 'These rates will replace the current schedule immediately when saved.'
                : 'Existing pricing will continue to apply until this date.'}
            </p>
          </div>
        </div>
      )}

      <div className="pricing-grid-wrapper">
        <div className="pricing-grid">
          <div className="pricing-grid-hour-label">
            <div className="pricing-grid-header-cell">Hour</div>
            {HOUR_ROWS.map((hour) => (
              <div key={hour} className="pricing-grid-hour-row">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {PRICING_COLUMNS.map((column) => (
            <div key={column.dayOfWeek} className="pricing-grid-column">
              <div className="pricing-grid-header-cell">{column.shortLabel}</div>
              <PricingColumnTrack
                dayOfWeek={column.dayOfWeek}
                blocks={displayBlocks}
                editMode={editMode}
                minRate={minRate}
                maxRate={maxRate}
                selectedBlockId={editMode ? selectedBlockId : null}
                onSelectBlock={setSelectedBlockId}
                onUpdateBlocks={editMode ? updateDraftBlocks : () => {}}
                onDeleteBlock={deleteBlock}
              />
            </div>
          ))}
        </div>
      </div>

      {editMode && selectedBlock && (
        <form
          className="pricing-rate-editor"
          onSubmit={(e) => {
            e.preventDefault();
            updateSelectedRate();
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {PRICING_COLUMNS.find((c) => c.dayOfWeek === selectedBlock.dayOfWeek)?.label}:{' '}
            {formatMinutesRange(selectedBlock.startMinutes, selectedBlock.endMinutes)}
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            Hourly rate ($)
            <input
              type="number"
              className="ah-input"
              min={0}
              step={0.01}
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              style={{ width: 100 }}
            />
          </label>
          <button type="submit" className="ah-btn ah-btn-outline">
            Apply rate
          </button>
        </form>
      )}

      {!editMode && (
        <div className="pricing-preview-section">
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Preview pricing for a date</h3>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              Select a date to see which rate schedule applies.
            </p>
            <Calendar
              selectedDate={previewDate}
              onDateSelect={setPreviewDate}
              maxDaysFuture={365}
              disabled={false}
              markedDates={publicHolidayDates}
              markedLabel="Public holiday"
            />
          </div>
          <div className="pricing-preview-details">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {previewDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
              Schedule: {previewColumn?.label ?? 'Unknown'}
              {publicHolidayDates.has(dateKey(previewDate)) && ' (marked public holiday)'}
            </div>
            {previewBlocks.length === 0 ? (
              <div className="muted">No pricing rules for this day.</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {previewBlocks.map((block) => (
                  <div
                    key={block.localId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: '#f8f9fa',
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    <span>{formatMinutesRange(block.startMinutes, block.endMinutes)}</span>
                    <span style={{ fontWeight: 600 }}>${block.hourlyRate.toFixed(2)}/hr</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {displayBlocks.length === 0 && !editMode && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <div className="muted">No pricing rules configured. Enter edit mode to set up weekly rates.</div>
        </div>
      )}

      {showExitConfirm && (
        <div className="pricing-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="pricing-exit-title">
          <div className="pricing-modal">
            <h3 id="pricing-exit-title" style={{ marginTop: 0 }}>Save pricing changes?</h3>
            <p style={{ fontSize: 14, lineHeight: 1.5 }}>
              All {PRICING_COLUMNS.length} day columns have full 24-hour coverage. Save this pricing schedule
              effective from{' '}
              <strong>
                {effectiveFromDate.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </strong>
              ?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" className="ah-btn ah-btn-outline" onClick={() => setShowExitConfirm(false)} disabled={saving}>
                Keep editing
              </button>
              <button type="button" className="ah-btn" onClick={confirmSaveAndExit} disabled={saving}>
                {saving ? 'Saving…' : 'Confirm & save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { PUBLIC_HOLIDAY_DAY };
